import React from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import ImageUploader from "./ImageUploader";
import { TextField } from "./AdminUI";

const TYPES = [
  { key: "text_over_image", label: "Text over image (full-bleed)" },
  { key: "image_full",       label: "Full-width image" },
  { key: "image_left",       label: "Image left · text right" },
  { key: "image_right",      label: "Image right · text left" },
  { key: "text_only",        label: "Text only (optional bg color)" },
  { key: "gallery",          label: "Gallery grid" },
  { key: "quote",            label: "Quote (dark)" },
];

const empty = (type) => {
  const base = { type };
  if (type === "text_over_image") return { ...base, image: "", eyebrow: "", heading: "", body: "", text_position: "center" };
  if (type === "image_full")     return { ...base, image: "", caption: "" };
  if (type === "image_left" || type === "image_right") return { ...base, image: "", eyebrow: "", heading: "", body: "" };
  if (type === "text_only")      return { ...base, eyebrow: "", heading: "", body: "", bg_color: "" };
  if (type === "gallery")        return { ...base, heading: "", images: [] };
  if (type === "quote")          return { ...base, quote: "", author: "" };
  return base;
};

const ContentBlocksEditor = ({ blocks = [], onChange }) => {
  const upd = (i, patch) => { const n = [...blocks]; n[i] = { ...n[i], ...patch }; onChange(n); };
  const rm  = (i) => onChange(blocks.filter((_, k) => k !== i));
  const mv  = (i, dir) => { const n = [...blocks]; const j = i + dir; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  const add = (type) => onChange([...blocks, empty(type)]);

  return (
    <div className="space-y-3">
      {blocks.length === 0 && (
        <div className="text-sm text-[color:var(--muted)] p-4 rounded-lg border border-dashed border-black/15">
          No blocks yet. Add your first section below.
        </div>
      )}
      {blocks.map((b, i) => (
        <div key={i} className="rounded-xl border border-black/10 bg-white p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--muted)] font-medium">
              Block {i + 1} · {TYPES.find(t => t.key === b.type)?.label || b.type}
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => mv(i, -1)} className="w-8 h-8 rounded-full border border-black/15 grid place-items-center" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => mv(i, +1)} className="w-8 h-8 rounded-full border border-black/15 grid place-items-center" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => rm(i)} className="w-8 h-8 rounded-full border border-black/15 grid place-items-center text-[color:var(--coral)]" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>

          {(b.type === "text_over_image" || b.type === "image_full" || b.type === "image_left" || b.type === "image_right") && (
            <ImageUploader label="Image" value={b.image} onChange={(v) => upd(i, { image: v })} aspect="aspect-[16/9]" />
          )}

          {(b.type === "text_over_image" || b.type === "image_left" || b.type === "image_right" || b.type === "text_only") && (
            <div className="mt-3 grid md:grid-cols-2 gap-3">
              <TextField label="Eyebrow (small caps label)" value={b.eyebrow} onChange={(v) => upd(i, { eyebrow: v })} />
              <TextField label="Heading" value={b.heading} onChange={(v) => upd(i, { heading: v })} />
            </div>
          )}

          {(b.type === "text_over_image" || b.type === "image_left" || b.type === "image_right" || b.type === "text_only") && (
            <div className="mt-3">
              <TextField label="Body text" textarea rows={4} value={b.body} onChange={(v) => upd(i, { body: v })} />
            </div>
          )}

          {b.type === "text_over_image" && (
            <div className="mt-3">
              <div className="text-xs uppercase tracking-wider text-[color:var(--muted)] mb-2">Text position on image</div>
              <div className="flex gap-2">
                {["left", "center", "right"].map((p) => (
                  <button key={p} type="button" onClick={() => upd(i, { text_position: p })}
                    className={`px-3 py-1.5 rounded-full text-sm border ${b.text_position === p ? "bg-[color:var(--ink)] text-[color:var(--bg)] border-[color:var(--ink)]" : "border-black/20"}`}>{p}</button>
                ))}
              </div>
            </div>
          )}

          {b.type === "text_only" && (
            <div className="mt-3">
              <TextField label="Background color (hex, optional)" value={b.bg_color} onChange={(v) => upd(i, { bg_color: v })} placeholder="#F7E9D9" />
            </div>
          )}

          {b.type === "image_full" && (
            <div className="mt-3">
              <TextField label="Caption (italic, optional)" value={b.caption} onChange={(v) => upd(i, { caption: v })} />
            </div>
          )}

          {b.type === "gallery" && (
            <>
              <TextField label="Heading (optional)" value={b.heading} onChange={(v) => upd(i, { heading: v })} />
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(b.images || []).map((src, j) => (
                  <div key={j} className="space-y-2">
                    <ImageUploader label="" value={src} onChange={(v) => { const imgs = [...(b.images || [])]; imgs[j] = v; upd(i, { images: imgs }); }} aspect="aspect-[4/3]" />
                    <button type="button" onClick={() => { const imgs = (b.images || []).filter((_, k) => k !== j); upd(i, { images: imgs }); }} className="text-xs text-[color:var(--coral)]">Remove</button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => upd(i, { images: [...(b.images || []), ""] })} className="mt-3 text-sm text-[color:var(--coral)] inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add image</button>
            </>
          )}

          {b.type === "quote" && (
            <div className="grid md:grid-cols-2 gap-3">
              <TextField label="Quote" textarea rows={3} value={b.quote} onChange={(v) => upd(i, { quote: v })} />
              <TextField label="Author" value={b.author} onChange={(v) => upd(i, { author: v })} />
            </div>
          )}
        </div>
      ))}

      <div className="pt-2">
        <div className="text-xs uppercase tracking-wider text-[color:var(--muted)] mb-2">Add block</div>
        <div className="flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <button key={t.key} type="button" onClick={() => add(t.key)} className="px-3 py-1.5 rounded-full text-sm border border-black/20 hover:border-black/50 transition-colors">+ {t.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContentBlocksEditor;
